import { OutputTemplateDocumentType } from "@prisma/client";
import { useState } from "react";

import { PrintSourceChoice } from "~/modules/orders/order-documents/PrintSourceChoice";
import { useOutputTemplatesEnabled } from "~/modules/output-templates/useOutputTemplatesEnabled";
import { Radio } from "~/ui";
import { api } from "~/utils/api";

/**
 * "Print with: built-in document / custom template", for every order document (SC-3349).
 *
 * SC-3763 wrote this inline in the bill-of-lading form. Ten documents now offer the same choice,
 * and a radio copied ten times is ten places for the gating to drift — the flag check, the
 * availability check, the disabled state and the explanation all have to agree every time, and
 * the ones that matter are the gates.
 *
 * BOTH GATES, as everywhere this feature appears. `useOutputTemplatesEnabled` says whether the org
 * is in the beta at all; the query says whether it has assigned a template to THIS document. Flag
 * off renders nothing, so the form is exactly what it was. Flag on with nothing assigned shows the
 * option disabled with the reason, which is more useful than hiding a feature the org is entitled
 * to.
 *
 * The availability query is one call listing every printable slot rather than a boolean per
 * document, so a page with several document modals does not make several round trips.
 */

interface Props {
  documentType: OutputTemplateDocumentType;
  value: boolean;
  onChange: (useOutputTemplate: boolean) => void;
  /**
   * Told to the operator when the template is chosen, because the modal's document options below
   * are drawn from the order instead. Omitted by documents with no such options.
   */
  legacyOptionsNote?: boolean;
  /**
   * Opts this form into offering ShelfCycle's standard template when the org has none of its own
   * (SC-3869), and is told whether that option is currently chosen.
   *
   * OPT-IN RATHER THAN AUTOMATIC, because choosing it has a consequence the form has to carry:
   * the print must set the template up first, and only this form's submit can sequence that
   * before printing. A form that has not opted in never sees the option, so it cannot offer
   * something it would not then act on. Today only the bill of lading does, which is also the
   * only document ShelfCycle publishes a base for.
   */
  onBaseChosenChange?: (chosen: boolean) => void;
  /** This source cannot be rendered faithfully by the assigned custom template path. */
  customTemplateDisabledReason?: string;
}

const WITH_OPTIONS_NOTE =
  "Choose the built-in document or your organization's custom template. A custom template is drawn from " +
  "the order's own data, so edits to built-in document options do not apply to it.";
const WITHOUT_OPTIONS_NOTE = "Choose the built-in document or your organization's custom template.";
const UNAVAILABLE_NOTE = "No custom template is assigned to this document yet. Assign one under Templates to use it here.";
/** Offered instead of the note above when ShelfCycle publishes one for this document. */
const BASE_AVAILABLE_NOTE =
  "You have no custom template for this document yet. Choose ShelfCycle's standard and it will be set up as your " +
  "custom template when you print — you can edit your copy afterwards under Custom Outputs.";

/** The option currently chosen. "base" exists only while the org has nothing of its own. */
type PrintSourceValue = "legacy" | "template" | "base";

export const PrintSourceRadio = ({
  documentType,
  value,
  onChange,
  legacyOptionsNote,
  onBaseChosenChange,
  customTemplateDisabledReason,
}: Props): JSX.Element | null => {
  const outputTemplatesEnabledForOrg = useOutputTemplatesEnabled();
  const { data: printableDocumentTypes } = api.orders.printableOrderDocumentTypes.useQuery(undefined, {
    enabled: outputTemplatesEnabledForOrg,
  });
  const { data: baseDocumentTypes } = api.orders.baseDocumentTypes.useQuery(undefined, {
    // Only asked by a form that can act on the answer, and only while it could matter.
    enabled: outputTemplatesEnabledForOrg && onBaseChosenChange !== undefined,
  });
  const [baseChosen, setBaseChosen] = useState(false);

  if (!outputTemplatesEnabledForOrg) {
    return null;
  }

  const hasAssignedTemplate = (printableDocumentTypes ?? []).includes(documentType);
  /**
   * The base is offered only while the org has nothing of its own for this document. Once the
   * first print has set the fork up, this goes away on its own: the fork IS the org's custom
   * template, so `hasAssignedTemplate` becomes true and the ordinary two-option choice returns.
   */
  const baseOffered = !hasAssignedTemplate && onBaseChosenChange !== undefined && (baseDocumentTypes ?? []).includes(documentType);

  // Explain source incompatibility before generic template availability and modal-option guidance.
  let description = baseOffered ? BASE_AVAILABLE_NOTE : UNAVAILABLE_NOTE;
  if (customTemplateDisabledReason) {
    description = customTemplateDisabledReason;
  } else if (hasAssignedTemplate && legacyOptionsNote) {
    description = WITH_OPTIONS_NOTE;
  } else if (hasAssignedTemplate) {
    description = WITHOUT_OPTIONS_NOTE;
  }

  /**
   * `value` is the form's "use a template" boolean, which cannot by itself distinguish the two
   * template options, so which one is chosen is held here and reported upward. Choosing anything
   * else clears it, so an operator who changes their mind does not silently leave the adoption
   * armed.
   */
  let selected: PrintSourceValue = "legacy";
  if (value) {
    selected = baseChosen && baseOffered ? "base" : "template";
  }

  const choose = (next: string) => {
    const chosenBase = next === "base";
    setBaseChosen(chosenBase);
    onBaseChosenChange?.(chosenBase);
    onChange(next !== "legacy");
  };

  return (
    <PrintSourceChoice selected={selected} onSelect={choose} legacyLabel="Built-in document" description={description}>
      {baseOffered ? (
        <Radio
          value="base"
          label="ShelfCycle's standard — sets up your copy when you print"
          disabled={Boolean(customTemplateDisabledReason)}
        />
      ) : (
        <Radio value="template" label="Custom template" disabled={!hasAssignedTemplate || Boolean(customTemplateDisabledReason)} />
      )}
    </PrintSourceChoice>
  );
};
